// Types & Interfaces
import { TType } from "../types";
import { IDesiredVehicle, IPhone, ISalesLeadErrors, TCustomerType } from "@/DataPages/context/crm/interfaces";

// Constants
import { TYPE_OF_PHONES, PROSPECT_INFORMATION_TAB } from "../constants";
import POSITIONS from "@/Constants/positions";

// Helpers
import { getSubPosition } from "@/Common/helpers/Positions";
import { or } from "@/Common/helpers/sonar";

// Services
import { getUsersSelect } from "@/Common/api/users";

function sortDropdownOptions(options: TType[]): TType[] {
	return [...options].sort((firstType: TType, secondType: TType) => (firstType.type ?? "")?.localeCompare(secondType.type));
}

function createStatusByFranchises(franchises: string[]): TType[] {
	return franchises.map((franchise: string) => ({ type: `Working ${franchise}` }));
}

function isNewStatus(newStatus: string, allStatuses: TType[]): boolean {
	if (newStatus === "") {
		return false;
	}

	const foundStatus = allStatuses.find((status) => status.type === newStatus);
	return !Boolean(foundStatus);
}

function defineStatuses(franchises: string[]) {
	const defaultStatus = PROSPECT_INFORMATION_TAB.statuses;
	const franchiseStatus = createStatusByFranchises(franchises);

	return [...defaultStatus, ...franchiseStatus];
}

function anyErrorInTab(tabObject: Record<string, boolean>): boolean {
	return Object.values(tabObject).some((error) => error);
}

function getPositionsByCategory(category: string) {
	const defaultPositions = {
		sales: [],
		bdc: [],
		manager: [],
	};

	if (!category) {
		return defaultPositions;
	}

	const categoryMap = {
		Sales: {
			sales: [
				POSITIONS.SALES.SALES_ASSOCIATE,
				getSubPosition(POSITIONS.SALES.SALES_ASSOCIATE, true),
				getSubPosition(POSITIONS.SALES.SALES_ASSOCIATE),
			],
			bdc: [
				POSITIONS.BDC.BDC_SALES_ASSOCIATE,
				POSITIONS.BDC.BDC_MANAGER,
				POSITIONS.SALES.SALES_ASSOCIATE,
				getSubPosition(POSITIONS.SALES.SALES_ASSOCIATE, true),
				getSubPosition(POSITIONS.SALES.SALES_ASSOCIATE),
				POSITIONS.SALES.SALES_MANAGER,
				POSITIONS.FINANCE.FINANCE_MANAGER,
				POSITIONS.FINANCE.FINANCE_DIRECTOR,
			],
			manager: [
				POSITIONS.SALES.SALES_MANAGER,
				POSITIONS.SALES.GENERAL_SALES_MANAGER,
				getSubPosition(POSITIONS.SALES.SALES_MANAGER, true),
				getSubPosition(POSITIONS.SALES.SALES_MANAGER),
			],
		},
		Service: {
			sales: [POSITIONS.SERVICE.SERVICE_ADVISOR],
			bdc: [POSITIONS.BDC.BDC_SERVICE_ASSOCIATE, POSITIONS.BDC.BDC_MANAGER],
			manager: [POSITIONS.SERVICE.SERVICE_MANAGER, POSITIONS.SERVICE.SERVICE_DIRECTOR],
		},
		Parts: {
			sales: [POSITIONS.PARTS.PARTS_COUNTER_ASSOCIATE],
			bdc: [POSITIONS.BDC.BDC_SERVICE_ASSOCIATE],
			manager: [POSITIONS.PARTS.PARTS_MANAGER, POSITIONS.PARTS.PARTS_DIRECTOR],
		},
	};

	return categoryMap[category as keyof typeof categoryMap] || defaultPositions;
}

function isIndividual(customerType: TCustomerType) {
	return customerType === "individual";
}

function isBusiness(customerType: TCustomerType) {
	return customerType === "business";
}

function transformCustomerType(customerType: TCustomerType) {
	if (!customerType.length) {
		return "";
	}

	const [firstLetter, ...rest] = customerType;

	return `${firstLetter.toUpperCase()}${rest.join("")}`;
}

function isAnyPhoneTypeAvailable(phoneNumbers: Array<IPhone>) {
	const availablePhoneTypeOptions = TYPE_OF_PHONES.filter((type) => !phoneNumbers.some((phone) => phone.type === type.type)).length;
	return availablePhoneTypeOptions > 0;
}

async function getUserOptions(locationId: string, positions: string[]) {
	const salesPerson = await getUsersSelect(locationId as string, positions, "ACTIVE");

	return salesPerson.map((user: { fullName: string }) => ({ type: user.fullName }));
}

function anyErrorInSaleLeadPopup(errors: Partial<ISalesLeadErrors>) {
	const { customer = {}, prospect = {}, vehicle = {} } = errors;

	const errorCustomer = Object.values(customer).some(Boolean);
	const errorProspect = Object.values(prospect).some(Boolean);
	const errorVehicle = Object.values(vehicle).some(Boolean);

	return or(errorCustomer, errorProspect, errorVehicle);
}

function determineDesiredVehicles(currentVehicles: IDesiredVehicle[], newDesiredVehicles: IDesiredVehicle[]): IDesiredVehicle[] {
	const currentVehiclesMap = new Map(currentVehicles.map((vehicle) => [vehicle._id, vehicle]));
	const newVehiclesMap = new Map(newDesiredVehicles.map((vehicle) => [vehicle._id, vehicle]));

	const allVehicles = new Map([...currentVehiclesMap, ...newVehiclesMap]);

	return Array.from(allVehicles.values());
}

function anyValidSearchInventoryInput({ type, stockNumber, vin, make, model, style, year }: IDesiredVehicle) {
	return Boolean(vin) || [type, stockNumber, make, model, style, year].some(Boolean);
}

function isNewVehicleAlreadySelected(newDesired: IDesiredVehicle, selectedVehicles: IDesiredVehicle[]): boolean {
	return selectedVehicles.some(({ make, model, stockNumber, style, type, vin, year }) => {
		return (
			make === newDesired.make &&
			model === newDesired.model &&
			stockNumber === newDesired.stockNumber &&
			style === newDesired.style &&
			type === newDesired.type &&
			vin === newDesired.vin &&
			year === newDesired.year
		);
	});
}

export {
	sortDropdownOptions,
	createStatusByFranchises,
	defineStatuses,
	isNewStatus,
	anyErrorInTab,
	getPositionsByCategory,
	isIndividual,
	isBusiness,
	transformCustomerType,
	isAnyPhoneTypeAvailable,
	getUserOptions,
	anyErrorInSaleLeadPopup,
	determineDesiredVehicles,
	anyValidSearchInventoryInput,
	isNewVehicleAlreadySelected,
};
